import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider as MuiDivider,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  People as PeopleIcon,
  Visibility as ViewIcon,
  Restore as RestoreIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Schedule as ScheduleIcon,
  Tune as TuneIcon,
  ToggleOn as ToggleOnIcon
} from '@mui/icons-material';
import api, { getStaticAssetUrl } from '../../services/api';
import MemberManagement from './service-groups/MemberManagement';
import ServiceGroupTimesOfService from './service-groups/ServiceGroupTimesOfService';
import ServiceGroupCBCSettings from './service-groups/ServiceGroupCBCSettings';
import ServiceGroupSafetyMonitoring from './service-groups/ServiceGroupSafetyMonitoring';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ServiceGroupParasocialSettings from './service-groups/ServiceGroupParasocialSettings';
import ServiceGroupAssessmentSettings from './service-groups/ServiceGroupAssessmentSettings';
import ServiceGroupFeatureOverrides from './service-groups/ServiceGroupFeatureOverrides';
import ServiceGroupCrisisResources from './service-groups/ServiceGroupCrisisResources';
import TriageQueueDialog from './service-groups/TriageQueueDialog';
import ServiceGroupSimulationSettings from './service-groups/ServiceGroupSimulationSettings';
import ServiceGroupClientUiSettings from './service-groups/ServiceGroupClientUiSettings';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

const ServiceGroupsManagement = () => {
  const [serviceGroups, setServiceGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [memberManagementOpen, setMemberManagementOpen] = useState(false);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [tosDialogOpen, setTosDialogOpen] = useState(false);
  const [selectedGroupForTos, setSelectedGroupForTos] = useState(null);
  const [cbcDialogOpen, setCbcDialogOpen] = useState(false);
  const [selectedGroupForCbc, setSelectedGroupForCbc] = useState(null);
  const [selectedGroupForSafety, setSelectedGroupForSafety] = useState(null);
  const [parasocialDialogOpen, setParasocialDialogOpen] = useState(false);
  const [selectedGroupForParasocial, setSelectedGroupForParasocial] = useState(null);
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [featureOverridesDialogOpen, setFeatureOverridesDialogOpen] = useState(false);
  const [selectedGroupForFeatures, setSelectedGroupForFeatures] = useState(null);
  const [selectedGroupForAssessment, setSelectedGroupForAssessment] = useState(null);
  const [triageDialogOpen, setTriageDialogOpen] = useState(false);
  const [selectedGroupForTriage, setSelectedGroupForTriage] = useState(null);
  const [simulationDialogOpen, setSimulationDialogOpen] = useState(false);
  const [selectedGroupForSimulation, setSelectedGroupForSimulation] = useState(null);
  const [clientUiDialogOpen, setClientUiDialogOpen] = useState(false);
  const [selectedGroupForClientUi, setSelectedGroupForClientUi] = useState(null);
  const [crisisDialogOpen, setCrisisDialogOpen] = useState(false);
  const [selectedGroupForCrisis, setSelectedGroupForCrisis] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    groupType: 'general',
    status: 'active',
    assignmentMode: 'manual_only',
    maxClientsPerTherapist: 20,
    trainingConfig: { enabled: false },
    advancedVoiceModeEnabled: false,
    serviceProfile: 'ai_forward',
    clientUIMode: 'full',
    programmeEnabled: false,
    showTherapistOnSlotPicker: true,
    requireIntakeBeforeBooking: true,
    branding: {
      clinicShortDescription: '',
      clinicLongDescription: '',
      heroImageUrl: '',
      signupLayout: 'therapist_first'
    }
  });

  useEffect(() => {
    fetchServiceGroups();
  }, [includeArchived]);

  const fetchServiceGroups = async () => {
    try {
      setLoading(true);
      const url = `/service-groups?includeArchived=${includeArchived.toString()}`;
      const response = await api.get(url);
      setServiceGroups(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching service groups:', err);
      setError(err.response?.data?.msg || 'Error fetching service groups');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (group = null) => {
    if (group) {
      setCurrentGroup(group);
      setFormData({
        name: group.name,
        description: group.description || '',
        groupType: group.groupType,
        status: group.status,
        assignmentMode: group.assignmentMode,
        maxClientsPerTherapist: group.maxClientsPerTherapist,
        trainingConfig: group.trainingConfig || { enabled: false },
        advancedVoiceModeEnabled: group.advancedVoiceModeEnabled === true,
        serviceProfile: group.serviceProfile || 'ai_forward',
        clientUIMode: group.clientUIMode || 'full',
        programmeEnabled: !!group.programEnrolmentConfig?.enabled,
        showTherapistOnSlotPicker: group.programEnrolmentConfig?.showTherapistOnSlotPicker !== false,
        requireIntakeBeforeBooking: group.programEnrolmentConfig?.requireIntakeBeforeBooking !== false,
        branding: {
          // Preserve existing logo / colour / subdomain so PUT doesn't wipe them
          ...(group.branding || {}),
          clinicShortDescription: group.branding?.clinicShortDescription || '',
          clinicLongDescription: group.branding?.clinicLongDescription || '',
          heroImageUrl: group.branding?.heroImageUrl || '',
          signupLayout: group.branding?.signupLayout || 'therapist_first'
        }
      });
      setLogoPreview(group.branding?.logoUrl || null);
    } else {
      setCurrentGroup(null);
      setFormData({
        name: '',
        description: '',
        groupType: 'general',
        status: 'active',
        assignmentMode: 'manual_only',
        maxClientsPerTherapist: 20,
        trainingConfig: { enabled: false },
        advancedVoiceModeEnabled: false,
        serviceProfile: 'ai_forward',
        clientUIMode: 'full',
        programmeEnabled: false,
        branding: {
          clinicShortDescription: '',
          clinicLongDescription: '',
          heroImageUrl: '',
          signupLayout: 'therapist_first'
        }
      });
      setLogoPreview(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentGroup(null);
    setLogoPreview(null);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Allowed types: JPEG, PNG, GIF, WebP, SVG');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB');
      return;
    }

    if (!currentGroup) {
      setError('Please save the service group first before uploading a logo');
      return;
    }

    setUploadingLogo(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('logo', file);

      // Don't set Content-Type header - axios will set it automatically with boundary
      const response = await api.post(
        `/service-groups/${currentGroup._id}/logo`,
        formDataToSend
      );

      if (response.data.success) {
        setLogoPreview(response.data.logoUrl);
        // Keep the form's branding in sync. handleSubmit PUTs formData.branding
        // wholesale, so without this the stale logoUrl captured at dialog-open
        // would overwrite the upload the moment the admin clicks Save.
        setFormData(prev => ({
          ...prev,
          branding: { ...(prev.branding || {}), logoUrl: response.data.logoUrl }
        }));
        setSuccess('Logo uploaded successfully');
        fetchServiceGroups(); // Refresh the list
      }
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err.response?.data?.msg || 'Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!currentGroup) return;

    if (!window.confirm('Are you sure you want to remove the logo?')) {
      return;
    }

    try {
      const response = await api.delete(`/service-groups/${currentGroup._id}/logo`);

      if (response.data.success) {
        setLogoPreview(null);
        // Clear it in the form too, else the subsequent Save writes the old
        // logoUrl straight back and the logo reappears.
        setFormData(prev => ({
          ...prev,
          branding: { ...(prev.branding || {}), logoUrl: null }
        }));
        setSuccess('Logo removed successfully');
        fetchServiceGroups();
      }
    } catch (err) {
      console.error('Error deleting logo:', err);
      setError(err.response?.data?.msg || 'Error deleting logo');
    }
  };

  const handleSubmit = async () => {
    try {
      const { serviceProfile, clientUIMode, programmeEnabled, showTherapistOnSlotPicker, requireIntakeBeforeBooking, ...rest } = formData;
      // Nested config: send only these flags so existing durations are preserved.
      rest.programEnrolmentConfig = {
        enabled: !!programmeEnabled,
        showTherapistOnSlotPicker: !!showTherapistOnSlotPicker,
        requireIntakeBeforeBooking: !!requireIntakeBeforeBooking
      };
      // intakeTemplateId is set from the Pre-Session template admin, not here —
      // omitted so this form never clears it.
      let groupId;
      if (currentGroup) {
        await api.put(`/service-groups/${currentGroup._id}`, rest);
        groupId = currentGroup._id;
      } else {
        const created = await api.post('/service-groups', rest);
        groupId = created.data?.data?._id || created.data?._id;
      }

      // Spec A — therapist-led service profile + client UI mode are written
      // through the feature-resolver admin route (separate audit trail via
      // PROFILE_CHANGED activity events).
      if (groupId) {
        try {
          await api.patch(`/admin/feature-resolver/service-groups/${groupId}`, {
            serviceProfile,
            clientUIMode
          });
        } catch (frErr) {
          console.error('Service profile patch failed:', frErr);
          setError(frErr.response?.data?.error || 'Service group saved, but profile/UI mode update failed');
          return;
        }
      }

      setSuccess(currentGroup ? 'Service group updated successfully' : 'Service group created successfully');
      handleCloseDialog();
      fetchServiceGroups();
    } catch (err) {
      console.error('Error saving service group:', err);
      setError(err.response?.data?.msg || 'Error saving service group');
    }
  };

  const handleArchive = async (groupId) => {
    if (!window.confirm('Are you sure you want to archive this service group?')) {
      return;
    }

    try {
      await api.post(`/service-groups/${groupId}/archive`);
      setSuccess('Service group archived successfully');
      fetchServiceGroups();
    } catch (err) {
      console.error('Error archiving service group:', err);
      setError(err.response?.data?.msg || 'Error archiving service group');
    }
  };

  const handleRestore = async (groupId) => {
    try {
      await api.post(`/service-groups/${groupId}/restore`);
      setSuccess('Service group restored successfully');
      fetchServiceGroups();
    } catch (err) {
      console.error('Error restoring service group:', err);
      setError(err.response?.data?.msg || 'Error restoring service group');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'recruiting':
        return 'info';
      case 'closed':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'clinical_trial':
        return 'Clinical Trial';
      case 'specialty_practice':
        return 'Specialty Practice';
      case 'research_cohort':
        return 'Research Cohort';
      case 'regional_service':
        return 'Regional Service';
      case 'training_program':
        return 'Training Program';
      case 'educational_institution':
        return 'Educational Institution';
      case 'general':
        return 'General';
      default:
        return type;
    }
  };

  const handleOpenMemberManagement = (group) => {
    setSelectedGroupForMembers(group);
    setMemberManagementOpen(true);
  };

  const handleCloseMemberManagement = () => {
    setMemberManagementOpen(false);
    setSelectedGroupForMembers(null);
    fetchServiceGroups(); // Refresh to update stats
  };

  const handleOpenTosDialog = (group) => {
    setSelectedGroupForTos(group);
    setTosDialogOpen(true);
  };

  const handleCloseTosDialog = () => {
    setTosDialogOpen(false);
    setSelectedGroupForTos(null);
  };

  const handleOpenCbcDialog = (group) => {
    setSelectedGroupForCbc(group);
    setCbcDialogOpen(true);
  };

  const handleCloseCbcDialog = () => {
    setCbcDialogOpen(false);
    setSelectedGroupForCbc(null);
  };

  const handleOpenParasocialDialog = (group) => {
    setSelectedGroupForParasocial(group);
    setParasocialDialogOpen(true);
  };

  const handleCloseParasocialDialog = () => {
    setParasocialDialogOpen(false);
    setSelectedGroupForParasocial(null);
  };

  const handleOpenClientUiDialog = (group) => {
    setSelectedGroupForClientUi(group);
    setClientUiDialogOpen(true);
  };

  const handleCloseClientUiDialog = () => {
    setClientUiDialogOpen(false);
    setSelectedGroupForClientUi(null);
  };

  if (loading && serviceGroups.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Service Groups Management</Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setIncludeArchived(!includeArchived)}
            >
              {includeArchived ? 'Hide Archived' : 'Show Archived'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Create Service Group
            </Button>
          </Box>
        </Box>

        {serviceGroups.length === 0 ? (
          <Alert severity="info">No service groups found.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Logo</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Clients</TableCell>
                  <TableCell align="right">Therapists</TableCell>
                  <TableCell align="right">Supervisors</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceGroups.map((group) => (
                  <TableRow key={group._id}>
                    <TableCell sx={{ width: 60 }}>
                      {group.branding?.logoUrl ? (
                        <Box
                          component="img"
                          src={getStaticAssetUrl(group.branding.logoUrl)}
                          alt={`${group.name} logo`}
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: 'contain',
                            borderRadius: 1
                          }}
                        />
                      ) : (
                        <ImageIcon sx={{ width: 40, height: 40, color: 'text.disabled' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {group.name}
                      </Typography>
                      {group.description && (
                        <Typography variant="caption" color="text.secondary">
                          {group.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={getTypeLabel(group.groupType)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={group.status.toUpperCase()}
                        size="small"
                        color={getStatusColor(group.status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {group.stats?.totalClients || 0}
                    </TableCell>
                    <TableCell align="right">
                      {group.stats?.totalTherapists || 0}
                    </TableCell>
                    <TableCell align="right">
                      {group.stats?.totalSupervisors || 0}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenMemberManagement(group)}
                        title="Manage Members"
                        color="primary"
                      >
                        <PeopleIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenTosDialog(group)}
                        title="Service Hours"
                        color="secondary"
                      >
                        <ScheduleIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenCbcDialog(group)}
                        title="CBC Settings"
                        color="info"
                      >
                        <TuneIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setSelectedGroupForSafety(group)}
                        title="Safety monitoring"
                        color="error"
                      >
                        <NotificationsActiveIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenParasocialDialog(group)}
                        title="Parasocial Detection Settings"
                        color="warning"
                      >
                        <TuneIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedGroupForFeatures(group); setFeatureOverridesDialogOpen(true); }}
                        title="Feature overrides"
                        color="primary"
                      >
                        <ToggleOnIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedGroupForAssessment(group); setAssessmentDialogOpen(true); }}
                        title="Assessment library"
                        color="primary"
                      >
                        <AssignmentTurnedInIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedGroupForTriage(group); setTriageDialogOpen(true); }}
                        title="Awaiting therapist queue"
                        color="secondary"
                      >
                        <GroupIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedGroupForSimulation(group); setSimulationDialogOpen(true); }}
                        title="Simulation library"
                        color="primary"
                      >
                        <SchoolIcon fontSize="small" />
                      </IconButton>
                      {/* Labelled rather than another icon: this is where voices,
                          avatars and persona representations are narrowed for a
                          group, and it was not findable among the icon row. */}
                      <Button
                        size="small"
                        onClick={() => handleOpenClientUiDialog(group)}
                        startIcon={<TuneIcon fontSize="small" />}
                        color="success"
                        sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                        title="Voices, avatars, persona representations and client UI for this group"
                      >
                        Voices &amp; UI
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedGroupForCrisis(group); setCrisisDialogOpen(true); }}
                        title="Crisis resources (per-audience contacts shown in the in-app banner + safety guard recovery)"
                        color="error"
                      >
                        <LocalHospitalIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(group)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {group.status !== 'archived' ? (
                        <IconButton
                          size="small"
                          onClick={() => handleArchive(group._id)}
                          title="Archive"
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleRestore(group._id)}
                          title="Restore"
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentGroup ? 'Edit Service Group' : 'Create Service Group'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <TextField
              label="Group Type"
              fullWidth
              select
              required
              value={formData.groupType}
              onChange={(e) => setFormData({ ...formData, groupType: e.target.value })}
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="clinical_trial">Clinical Trial</MenuItem>
              <MenuItem value="specialty_practice">Specialty Practice</MenuItem>
              <MenuItem value="research_cohort">Research Cohort</MenuItem>
              <MenuItem value="regional_service">Regional Service</MenuItem>
              <MenuItem value="training_program">Training Program</MenuItem>
              <MenuItem value="educational_institution">Educational Institution</MenuItem>
              {/* Required so existing platform-authoring SGs (created by
                  simulationResolver.getOrCreatePlatformGroup) can be edited
                  without silently losing their groupType. Admins shouldn't
                  normally create new ones here. */}
              <MenuItem value="platform_authoring">Platform Authoring (system)</MenuItem>
            </TextField>
            <TextField
              label="Status"
              fullWidth
              select
              required
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="recruiting">Recruiting</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </TextField>
            <TextField
              label="Assignment Mode"
              fullWidth
              select
              required
              value={formData.assignmentMode}
              onChange={(e) => setFormData({ ...formData, assignmentMode: e.target.value })}
            >
              <MenuItem value="manual_only">Manual Only</MenuItem>
              <MenuItem value="auto_assign">Auto Assign</MenuItem>
              <MenuItem value="waitlist">Waitlist</MenuItem>
            </TextField>

            {/* Spec A — Therapist-Led Mode service profile + client UI mode.
                Changes are audited via PROFILE_CHANGED activity events. */}
            <TextField
              label="Service Profile"
              fullWidth
              select
              required
              helperText="Therapist-Led Mode controls. Changes here are audited."
              value={formData.serviceProfile}
              onChange={(e) => {
                const sp = e.target.value;
                // Default the UI mode based on profile (admin can still override)
                const defaultUI = (sp === 'therapist_led' || sp === 'therapist_enhanced')
                  ? 'patient_portal' : 'full';
                setFormData({
                  ...formData,
                  serviceProfile: sp,
                  clientUIMode: formData.clientUIMode === 'patient_portal' || formData.clientUIMode === 'full'
                    ? defaultUI
                    : formData.clientUIMode
                });
              }}
            >
              <MenuItem value="ai_forward">AI-Forward (default — chatbot-led)</MenuItem>
              <MenuItem value="hybrid">Hybrid (AI + therapist sessions)</MenuItem>
              <MenuItem value="therapist_enhanced">Therapist-Enhanced (AI augmentation)</MenuItem>
              <MenuItem value="therapist_led">Therapist-Led (human primary, AI assistive)</MenuItem>
            </TextField>
            <TextField
              label="Client UI Mode"
              fullWidth
              select
              required
              helperText="Determines whether clients land on /chat or /portal after login."
              value={formData.clientUIMode}
              onChange={(e) => setFormData({ ...formData, clientUIMode: e.target.value })}
            >
              <MenuItem value="full">Full App (AI conversational interface)</MenuItem>
              <MenuItem value="patient_portal">Patient Portal (tasks, summaries, documents)</MenuItem>
            </TextField>

            {/* AD-02: turning this on makes signup create a ProgramEnrolment, which
                is what puts a client into the consent → book → attend → approve
                journey rather than straight into the app. */}
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.programmeEnabled}
                    onChange={(e) => setFormData({ ...formData, programmeEnabled: e.target.checked })}
                  />
                }
                label="Run a structured programme (enrolment journey)"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Clients signing up through this group's invitation are enrolled and must complete
                consent, then book an intake call and be approved by a therapist before the full app
                opens to them.
              </Typography>
              {formData.programmeEnabled && (
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.showTherapistOnSlotPicker}
                        onChange={(e) => setFormData({ ...formData, showTherapistOnSlotPicker: e.target.checked })}
                      />
                    }
                    label="Name the therapist on each bookable slot"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    On, clients see who each slot belongs to and choose accordingly. Off, the picker is a
                    list of times and the therapist is revealed once the slot is booked — which avoids one
                    clinician taking a disproportionate share when the pool is small.
                  </Typography>
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Switch
                        checked={!!formData.requireIntakeBeforeBooking}
                        onChange={(e) => setFormData({ ...formData, requireIntakeBeforeBooking: e.target.checked })}
                      />
                    }
                    label="Require consent and questionnaires before booking"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    On, a client can book their first appointment only after agreeing the consent form and
                    completing the required intake questionnaires, so the therapist has read them before
                    the call.
                  </Typography>
                </Box>
              )}

              {formData.programmeEnabled && formData.clientUIMode === 'full' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Set Client UI Mode to <strong>Patient Portal</strong> as well. It is the fallback for a
                  client who has no enrolment yet — left on Full App, anyone the enrolment step misses
                  lands in the full AI app having skipped consent and the intake call.
                </Alert>
              )}
            </Box>

            {/* Clinic profile — used on the public invitation/signup landing page. */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Clinic profile (signup landing page)</Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Clinic short description (≤300 chars)"
                  fullWidth multiline minRows={2} maxRows={3}
                  value={formData.branding?.clinicShortDescription || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    branding: { ...(formData.branding || {}), clinicShortDescription: e.target.value.slice(0, 300) }
                  })}
                  helperText={`${(formData.branding?.clinicShortDescription || '').length}/300`}
                />
                <TextField
                  label="Clinic long description (optional, ≤2000 chars)"
                  fullWidth multiline minRows={3} maxRows={6}
                  value={formData.branding?.clinicLongDescription || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    branding: { ...(formData.branding || {}), clinicLongDescription: e.target.value.slice(0, 2000) }
                  })}
                  helperText={`${(formData.branding?.clinicLongDescription || '').length}/2000`}
                />
                <TextField
                  label="Hero image URL (optional)"
                  fullWidth size="small"
                  placeholder="Paste a URL or upload via the hero-image button after saving"
                  value={formData.branding?.heroImageUrl || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    branding: { ...(formData.branding || {}), heroImageUrl: e.target.value }
                  })}
                />
                <TextField
                  label="Signup landing layout"
                  fullWidth select size="small"
                  value={formData.branding?.signupLayout || 'therapist_first'}
                  onChange={(e) => setFormData({
                    ...formData,
                    branding: { ...(formData.branding || {}), signupLayout: e.target.value }
                  })}
                  helperText="Default: therapist front and centre with clinic strip header"
                >
                  <MenuItem value="therapist_first">Therapist first (recommended)</MenuItem>
                  <MenuItem value="clinic_first">Clinic first</MenuItem>
                  <MenuItem value="balanced">Balanced</MenuItem>
                </TextField>
              </Stack>
            </Box>

            <TextField
              label="Max Clients Per Therapist"
              fullWidth
              type="number"
              required
              value={formData.maxClientsPerTherapist}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxClientsPerTherapist: parseInt(e.target.value, 10)
                })
              }
              inputProps={{ min: 1, max: 100 }}
            />

            {/* Advanced Voice Mode default for all clients in this group.
                Therapists can still override on/off in their own voice settings. */}
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={formData.advancedVoiceModeEnabled || false}
                  onChange={(e) => setFormData({
                    ...formData,
                    advancedVoiceModeEnabled: e.target.checked
                  })}
                />
              }
              label="Advanced Voice Mode (group default — therapist settings override)"
            />

            {/* Training Configuration - show for training-related group types */}
            {(formData.groupType === 'educational_institution' || formData.groupType === 'training_program') && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Training Configuration</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.trainingConfig?.enabled || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        trainingConfig: { ...(formData.trainingConfig || {}), enabled: e.target.checked }
                      })}
                    />
                  }
                  label="Enable Training Platform"
                />
                {formData.trainingConfig?.enabled && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    <TextField
                      label="Max Students" type="number" size="small"
                      value={formData.trainingConfig?.maxStudents ?? 500}
                      onChange={(e) => setFormData({
                        ...formData,
                        trainingConfig: { ...(formData.trainingConfig || {}), maxStudents: parseInt(e.target.value) || 500 }
                      })}
                      sx={{ width: 140 }}
                      inputProps={{ min: 1 }}
                    />
                    <TextField
                      label="Max Lecturers" type="number" size="small"
                      value={formData.trainingConfig?.maxLecturers ?? 50}
                      onChange={(e) => setFormData({
                        ...formData,
                        trainingConfig: { ...(formData.trainingConfig || {}), maxLecturers: parseInt(e.target.value) || 50 }
                      })}
                      sx={{ width: 140 }}
                      inputProps={{ min: 1 }}
                    />
                    <TextField
                      label="Organisation Display Name" size="small"
                      value={formData.trainingConfig?.branding?.organisationName || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        trainingConfig: {
                          ...(formData.trainingConfig || {}),
                          branding: { ...(formData.trainingConfig?.branding || {}), organisationName: e.target.value }
                        }
                      })}
                      sx={{ flex: 1, minWidth: 200 }}
                      placeholder="Displayed to students"
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Logo Upload Section - Only show when editing */}
            {currentGroup && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Service Group Logo
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  {logoPreview ? (
                    <Box
                      component="img"
                      src={getStaticAssetUrl(logoPreview)}
                      alt="Service group logo"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 0.5
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: 'divider',
                        bgcolor: 'action.hover'
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    </Box>
                  )}
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={uploadingLogo ? <CircularProgress size={16} /> : <UploadIcon />}
                      disabled={uploadingLogo}
                      size="small"
                    >
                      {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                        onChange={handleLogoUpload}
                      />
                    </Button>
                    {logoPreview && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        size="small"
                        onClick={handleDeleteLogo}
                      >
                        Remove Logo
                      </Button>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Max 5MB. Formats: JPEG, PNG, GIF, WebP, SVG
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {!currentGroup && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Save the service group first, then edit it to upload a logo.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {currentGroup ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Member Management Dialog */}
      <Dialog
        open={memberManagementOpen}
        onClose={handleCloseMemberManagement}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedGroupForMembers && (
            <MemberManagement
              serviceGroup={selectedGroupForMembers}
              token={null} // Will be passed from AdminDashboard if needed
              onClose={handleCloseMemberManagement}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Times of Service Override Dialog */}
      <ServiceGroupTimesOfService
        open={tosDialogOpen}
        onClose={handleCloseTosDialog}
        serviceGroup={selectedGroupForTos}
        onSave={handleCloseTosDialog}
      />

      {/* CBC Settings Dialog */}
      <Dialog
        open={cbcDialogOpen}
        onClose={handleCloseCbcDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle>
          CBC Settings - {selectedGroupForCbc?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedGroupForCbc && (
            <ServiceGroupCBCSettings
              groupId={selectedGroupForCbc._id}
              groupName={selectedGroupForCbc.name}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCbcDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Safety Monitoring Dialog */}
      <Dialog open={!!selectedGroupForSafety} onClose={() => setSelectedGroupForSafety(null)} maxWidth="md" fullWidth>
        <DialogTitle>Safety Monitoring - {selectedGroupForSafety?.name}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedGroupForSafety && (
            <ServiceGroupSafetyMonitoring groupId={selectedGroupForSafety._id} groupName={selectedGroupForSafety.name} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedGroupForSafety(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Parasocial Detection Settings Dialog */}
      <Dialog
        open={parasocialDialogOpen}
        onClose={handleCloseParasocialDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle>
          Parasocial Detection - {selectedGroupForParasocial?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedGroupForParasocial && (
            <ServiceGroupParasocialSettings
              groupId={selectedGroupForParasocial._id}
              groupName={selectedGroupForParasocial.name}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseParasocialDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Simulation library + methodology library + feedback visibility */}
      <Dialog
        open={simulationDialogOpen}
        onClose={() => setSimulationDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh' } }}
      >
        <DialogTitle>Simulation library — {selectedGroupForSimulation?.name}</DialogTitle>
        <DialogContent>
          {selectedGroupForSimulation && (
            <ServiceGroupSimulationSettings
              groupId={selectedGroupForSimulation._id}
              token={localStorage.getItem('token')}
              onClose={({ saved } = {}) => {
                setSimulationDialogOpen(false);
                if (saved) setSuccess('Simulation library saved');
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Awaiting-therapist triage queue */}
      <Dialog
        open={triageDialogOpen}
        onClose={() => setTriageDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh' } }}
      >
        <DialogTitle>Awaiting therapist — {selectedGroupForTriage?.name}</DialogTitle>
        <DialogContent>
          {selectedGroupForTriage && (
            <TriageQueueDialog
              groupId={selectedGroupForTriage._id}
              token={localStorage.getItem('token')}
              onClose={() => setTriageDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Feature overrides dialog */}
      <Dialog
        open={featureOverridesDialogOpen}
        onClose={() => setFeatureOverridesDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh' } }}
      >
        <DialogTitle>
          Feature overrides — {selectedGroupForFeatures?.name}
        </DialogTitle>
        <DialogContent>
          {selectedGroupForFeatures && (
            <ServiceGroupFeatureOverrides
              groupId={selectedGroupForFeatures._id}
              token={localStorage.getItem('token')}
              onClose={({ saved, changed } = {}) => {
                setFeatureOverridesDialogOpen(false);
                if (saved) setSuccess(`Feature overrides saved (${changed} change${changed === 1 ? '' : 's'})`);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Assessment library + defaults dialog */}
      <Dialog
        open={assessmentDialogOpen}
        onClose={() => setAssessmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh' } }}
      >
        <DialogTitle>
          Assessment library — {selectedGroupForAssessment?.name}
        </DialogTitle>
        <DialogContent>
          {selectedGroupForAssessment && (
            <ServiceGroupAssessmentSettings
              groupId={selectedGroupForAssessment._id}
              token={localStorage.getItem('token')}
              onClose={({ saved } = {}) => {
                setAssessmentDialogOpen(false);
                if (saved) setSuccess('Assessment library saved');
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Client UI & Voice/Avatar Settings Dialog (Meeting 3) */}
      <Dialog
        open={clientUiDialogOpen}
        onClose={handleCloseClientUiDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh' } }}
      >
        <DialogTitle>
          Voices, avatars &amp; client UI — {selectedGroupForClientUi?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedGroupForClientUi && (
            <ServiceGroupClientUiSettings
              groupId={selectedGroupForClientUi._id}
              groupName={selectedGroupForClientUi.name}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClientUiDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Crisis resources dialog (per-SG primary/secondary contacts, audience + i18n) */}
      <Dialog
        open={crisisDialogOpen}
        onClose={() => setCrisisDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh' } }}
      >
        <DialogTitle>Crisis resources — {selectedGroupForCrisis?.name}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedGroupForCrisis && (
            <ServiceGroupCrisisResources
              groupId={selectedGroupForCrisis._id}
              groupName={selectedGroupForCrisis.name}
              token={localStorage.getItem('token')}
              onClose={({ saved } = {}) => {
                setCrisisDialogOpen(false);
                if (saved) setSuccess('Crisis resources saved');
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ServiceGroupsManagement;
